import React, {useState} from 'react';
import { useUserContext } from "../../contexts";
import { getReadableDate } from '../../lib/utils';
import { getMembershipLevelLabel } from '../../lib/subscriptionCapabilities';
import './Membership.scss';
import { resumeSubscriptionFn } from '../../lib/api';

const Membership = ({cta}) => {

    //Custom Hooks
    const {user, reloadCurrentUser} = useUserContext();

    const [resuming, setResuming] = useState(false);

    const isMoreThanTwoYears = (expired_at) => {
        // Parsea la fecha 'expired_at' a un objeto Date
        const expiredDate = new Date(expired_at);
    
        // Obtén la fecha actual
        const currentDate = new Date();
    
        // Añade dos años a la fecha actual
        const twoYearsLater = new Date();
        twoYearsLater.setFullYear(currentDate.getFullYear() + 2);
    
        // Compara si 'expired_at' es mayor que la fecha actual más dos años
        return expiredDate > twoYearsLater;
    };

    const handleMembershipClick = () => {
        if(cta){
            cta();
        }
    };
    if(!user || !user.team || !user.team.subscription || !user.team.subscription.plan ){
        return null;
    }

    const getResumeDate = (lastPaidSub) => {
        if (!lastPaidSub) return null;
        return (
            lastPaidSub.resume_at ||
            lastPaidSub.resumes_at ||
            lastPaidSub.resume_date ||
            (lastPaidSub.pause_collection && lastPaidSub.pause_collection.resume_at) ||
            lastPaidSub.resume_on ||
            lastPaidSub.paused_until ||
            null
        );
    };

    const currentSub = user?.team?.subscription ?? null;
    const lastPaidSub = user && user.team ? user.team.last_paid_subscription : null;
    const resumeDate = (currentSub && currentSub.paused_until) ? currentSub.paused_until : getResumeDate(lastPaidSub);
    const membershipPlan = user?.team?.subscription?.plan ?? null;
    const membershipLevelLabel = getMembershipLevelLabel(membershipPlan);
    const isProMembershipLevel = membershipLevelLabel === 'PRO';
    const isPaidMembershipLevel = Number(membershipPlan?.price ?? 0) > 0;

    const getPlanLabel = (plan) => {
        if (!plan) return 'Subscription';
        const price = plan.price;
        const productName = (plan.product && plan.product.name) ? String(plan.product.name).toUpperCase() : '';
        if (+price === 0) return 'Sample';
        if (productName.includes('ENTERPRISE')) return 'Team';
        return 'Pro';
    };

    const resumePlanLabel = getPlanLabel(lastPaidSub && lastPaidSub.plan ? lastPaidSub.plan : (user.team.subscription && user.team.subscription.plan));

    const provider = (currentSub && currentSub.stripe === true)
        ? 'stripe'
        : ((currentSub && currentSub.paypal === true)
            ? 'paypal'
            : (lastPaidSub && lastPaidSub.stripe === true
                ? 'stripe'
                : (lastPaidSub && lastPaidSub.paypal === true ? 'paypal' : null)));
    const planId = (currentSub && currentSub.plan && currentSub.plan.id) ? currentSub.plan.id : (lastPaidSub && lastPaidSub.plan ? lastPaidSub.plan.id : null);

    const handleResume = () => {
        if (!provider || !planId) return;
        const postData = { provider, planId };
        setResuming(true);
        resumeSubscriptionFn(postData)
            .then(data => {
                if (data && data.url) {
                    document.location.href = data.url;
                    return;
                }
                // No redirect: refresh user
                try { reloadCurrentUser && reloadCurrentUser(); } catch(_) {}
            })
            .catch(() => {
                // keep UI, try to refresh user anyway
                try { reloadCurrentUser && reloadCurrentUser(); } catch(_) {}
            })
            .finally(() => {
                setResuming(false);
            });
    };

    const isPaused = !!(
        (currentSub && (currentSub.paused === true || currentSub.state === 'paused')) ||
        (lastPaidSub && (lastPaidSub.state === 'paused' || lastPaidSub.paused === true || !!resumeDate))
    );

    return (
        <>
            <div className={`membership m-b-8 ${cta?'cta_member':''}`} onClick={handleMembershipClick}>
                {isProMembershipLevel && <span className="glyphicon glyphicon-star star" ></span>}
                {!isProMembershipLevel && isPaidMembershipLevel && <span className="glyphicon glyphicon-star-half star" ></span>}
                {`User Level: ${membershipLevelLabel}`}
                {user.isEnterprise && <div className="chip accent">team</div>}
                { user.team.subscription.state === 'canceled' && user.team.subscription.expired_at && 
                <>
                    <div className="chip canceled">canceled</div>
                    { !isMoreThanTwoYears(user.team.subscription.expired_at) && 
                    <div className="chip">
                        Access remains active until {getReadableDate(user.team.subscription.expired_at)}
                    </div>
                    }
                </>
                }
                { user.team.subscription.state === 'pending' && user.team.subscription.expired_at && 
                <>
                    <div className="chip">pending</div>
                    { !isMoreThanTwoYears(user.team.subscription.expired_at) && 
                    <div className="chip">
                        Your membership has a pending payment.
                    </div>
                    }
                </>
                }
            </div>
            { isPaused && user.team.subscription.state !== 'canceled' &&
                <div className="membership-resume">
                    <div className="resume-header">
                        <span className="glyphicon glyphicon-pause"></span>
                        {`${resumePlanLabel} subscription is paused`}
                    </div>
                    <div className="resume-body">
                        {resumeDate
                            ? <>Resumes on {getReadableDate(resumeDate)}.</>
                            : <>Paused indefinitely.</>
                        }
                        {provider && planId &&
                            <button className="resume-cta accent" onClick={handleResume} disabled={resuming}>
                                {resuming && <span className="glyphicon glyphicon-hour-glass"></span>}
                                {resuming ? 'Resuming…' : 'Resume Subscription'}
                            </button>
                        }
                    </div>
                </div>
            }
        </>
    );
};

export default Membership;
